import { subscribeToSSE } from "@albertine/api-client";
import { EventType, type StreamChunk } from "@tanstack/ai/client";
import type { ConnectionAdapter } from "@tanstack/ai-react";
import { z } from "zod";

export const ConciergeChatEventNameSchema = z.enum([
    "message-deleted",
    "message-edited",
    "message-sent",
]);

export type ConciergeChatEventName = z.infer<typeof ConciergeChatEventNameSchema>;

export const ConciergeChatEventSchema = z.object({
    data: z.unknown(),
    eventId: z.string().optional(),
});

export type ConciergeChatEvent = z.infer<typeof ConciergeChatEventSchema>;

type AccessTokenGetter = (options?: {
    forceRefresh?: boolean;
}) => Promise<string | null>;

type CreateConciergeSseConnectionOptions = {
    getAccessToken?: AccessTokenGetter;
    onUnauthorized?: () => void;
    url: string;
};

export function createConciergeSseConnection(
    options: CreateConciergeSseConnectionOptions,
): ConnectionAdapter {
    return {
        subscribe: (abortSignal) =>
            subscribeToChatEvents(
                options.url,
                abortSignal,
                options.getAccessToken,
                options.onUnauthorized,
            ),
        send: () => Promise.resolve(),
    };
}

async function* subscribeToChatEvents(
    url: string,
    abortSignal?: AbortSignal,
    getAccessToken?: AccessTokenGetter,
    onUnauthorized?: () => void,
): AsyncIterable<StreamChunk> {
    const stream = subscribeToSSE({
        url,
        abortSignal,
        getAccessToken,
        onUnauthorized,
        shouldRetry: () => false,
    });
    for await (const event of stream) {
        const eventName = ConciergeChatEventNameSchema.safeParse(event.type);
        if (!eventName.success) {
            continue;
        }

        yield {
            type: EventType.CUSTOM,
            name: eventName.data,
            value: ConciergeChatEventSchema.parse({
                data: JSON.parse(event.data),
                eventId: event.id,
            }),
        };
    }
}
